import { useState } from 'react';
import { endOfDay, startOfDay, subDays } from 'date-fns';

import { DatePicker } from '@/components/form/inputs/date-picker';
import { getLocalTimezoneAbbreviation } from '@/components/pages/assets/capacity-test/utils';
import { useNow } from '@/hooks/useNow';

export type DateRangeFilterProps = {
  value: DateRange;
  onChange: (value: DateRange) => void;
};

export type DateRange = { from?: Date; to?: Date };

type PresetKey = '7d' | '14d' | '30d' | '90d';

const PRESETS: { key: PresetKey; label: string; days: number }[] = [
  { key: '7d', label: '7d', days: 7 },
  { key: '14d', label: '14d', days: 14 },
  { key: '30d', label: '30d', days: 30 },
  { key: '90d', label: '90d', days: 90 },
];

export function DateRangeFilter({ value, onChange }: DateRangeFilterProps) {
  const now = useNow();
  const [activePreset, setActivePreset] = useState<PresetKey | null>(null);

  function handlePresetClick(preset: PresetKey, days: number) {
    const from = startOfDay(subDays(now, days - 1));
    const to = endOfDay(now);
    onChange({ from, to });
    setActivePreset(preset);
  }

  function handleFromChange(date: Date | undefined) {
    onChange({ from: date, to: value.to });
    setActivePreset(null);
  }

  function handleToChange(date: Date | undefined) {
    onChange({ from: value.from, to: date });
    setActivePreset(null);
  }

  const timezone = getLocalTimezoneAbbreviation(now);

  const fromDisabledDates = [{ after: value.to ?? now }, { after: now }];

  const toDisabledDates = value.from
    ? [{ before: value.from }, { after: now }]
    : [{ after: now }];

  return (
    <div className="flex flex-col">
      <label className="mb-1 block text-xs font-medium text-gray-600">
        Time Window ({timezone})
      </label>

      <div className="flex items-center gap-2">
        <div className="flex gap-1">
          {PRESETS.map((preset) => (
            <button
              key={preset.key}
              type="button"
              onClick={() => handlePresetClick(preset.key, preset.days)}
              className={`rounded px-2 py-1 text-xs font-medium transition-colors ${
                activePreset === preset.key
                  ? 'bg-primary-50 text-primary-700'
                  : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
              }`}
            >
              {preset.label}
            </button>
          ))}
        </div>

        <DatePicker
          value={value.from}
          onSelect={handleFromChange}
          placeholder="Start date"
          disabledDates={fromDisabledDates}
        />

        <DatePicker
          value={value.to}
          onSelect={handleToChange}
          placeholder="End date"
          disabledDates={toDisabledDates}
        />
      </div>
    </div>
  );
}
